import { defineAsyncComponent, type Component } from 'vue';
import type { Features } from '~/provides/features.types';
import type { AxiosPromise } from 'axios';
import teamsClient from '~/services/teamsClient';

export type StatusFetcher = (teamId: number) => AxiosPromise<{ status: string }>;

export interface Integration {
    slug: string;
    name: string;
    tagline: string;
    featureFlag?: keyof Features;
    settingsPath?: string;
    overview: string;
    features: string[];
    logo?: Component;
    url?: string;
    getStatus?: StatusFetcher;
    documentation?: Component;
    comingSoon?: boolean;
}

export const integrations: Integration[] = [
    {
        slug: 'acuity',
        name: 'Acuity',
        tagline: 'Create contacts from meetings scheduled via Acuity.',
        featureFlag: 'acuity_integration',
        settingsPath: '/settings/integrations#/acuity',
        overview:
            'When the Acuity integration is connected, calendar appointments scheduled via Acuity will automatically create new contacts in Voxie. Capture appointment details and customer information to enable follow-up SMS communications.',
        features: [
            'Automatic contact creation from appointments',
            'Phone number, email, and name collection',
            'Appointment date and type tracking',
            'Franchise Hub group mapping for appointment types',
            'Custom data mapping support',
        ],
        logo: defineAsyncComponent(() => import('./logos/AcuityLogo.vue')),
        url: 'https://acuityscheduling.com',
        getStatus: (teamId) => teamsClient.getAcuityConnectionStatus(teamId),
        documentation: defineAsyncComponent(() => import('../integrations/acuity/AcuityDocumentation.vue')),
    },
    {
        slug: 'amplitude',
        name: 'Amplitude',
        tagline: 'Sync Amplitude audiences and events with Voxie.',
        featureFlag: 'amplitude_integration',
        settingsPath: '/settings/integrations#/amplitude',
        overview:
            'Connect Amplitude to Voxie to leverage your analytics platform for SMS marketing. Sync cohorts as Voxie External Lists, stream contact data, and send engagement events back to Amplitude.',
        features: [
            'Sync Amplitude cohorts to Voxie external lists',
            'Create and update contacts from Amplitude event streaming',
            'Send Voxie subscription status changes back to Amplitude',
            'Forward message events (sent, clicks, replies) to Amplitude',
        ],
        logo: defineAsyncComponent(() => import('./logos/AmplitudeLogo.vue')),
        url: 'https://amplitude.com',
        getStatus: (teamId) => teamsClient.getAmplitudeStatus(teamId),
        documentation: defineAsyncComponent(() => import('../integrations/amplitude/AmplitudeDocumentation.vue')),
    },
    {
        slug: 'api-destinations',
        name: 'API Destinations',
        tagline: 'Send Voxie event data to external APIs in real-time.',
        featureFlag: 'api_destinations',
        settingsPath: '/settings/integrations#/api-destinations',
        overview:
            'API Destinations allow you to push Voxie events to external systems in real-time. Configure webhooks to send contact updates, message events, and other data to your own APIs or third-party services.',
        features: [
            'Real-time event delivery',
            'Configurable authentication methods',
            'Multiple destination support',
            'Delivery status monitoring',
            'Retry and failure handling',
        ],
    },
    {
        slug: 'calendly',
        name: 'Calendly',
        tagline: 'Create contacts from meetings scheduled via Calendly.',
        featureFlag: 'calendly_integration',
        settingsPath: '/settings/integrations#/calendly',
        overview:
            'When the Calendly integration is connected, meeting requests scheduled via Calendly will automatically create new contacts in Voxie. Voxie collects contact details including phone number, name, email, and time-zone from meeting invitees.',
        features: [
            'Automatic contact creation from meeting bookings',
            'Phone number, email, and name collection',
            'Meeting status and reschedule URL tracking',
            'Franchise Hub group mapping for event types',
        ],
        logo: defineAsyncComponent(() => import('./logos/CalendlyLogo.vue')),
        url: 'https://calendly.com',
        getStatus: (teamId) => teamsClient.getCalendlyConnectionStatus(teamId),
        documentation: defineAsyncComponent(() => import('../integrations/calendly/CalendlyDocumentation.vue')),
    },
    {
        slug: 'contact-collector',
        name: 'Contact Collector',
        tagline: 'Capture leads with embeddable signup forms.',
        settingsPath: '/settings/integrations#/contact-collector',
        overview:
            'Contact Collector provides embeddable forms that capture new contacts directly into Voxie. Add signup forms to your website or landing pages to grow your SMS subscriber list with proper opt-in consent.',
        features: [
            'Embeddable signup forms',
            'Customizable form fields',
            'Automatic opt-in consent collection',
            'Direct contact creation in Voxie',
        ],
    },
    {
        slug: 'ftp-pull',
        name: 'FTP Pull',
        tagline: 'Automatically import data from your SFTP servers.',
        settingsPath: '/settings/integrations#/ftp-pull',
        overview:
            'The FTP Pull integration allows Voxie to automatically pull data from your SFTP servers and ingest it for processing. Configure URLs to pull contacts, conversions, or group data on a scheduled basis.',
        features: [
            'Automated SFTP data retrieval',
            'Contact import from CSV files',
            'Conversion tracking data import',
            'Group membership data sync',
            'Incremental file processing',
        ],
        getStatus: (teamId) => teamsClient.getFtpPullStatus(teamId),
        documentation: defineAsyncComponent(() => import('../integrations/ftp-pull/FtpPullDocumentation.vue')),
    },
    {
        slug: 'hubspot',
        name: 'HubSpot',
        tagline: 'Connect your HubSpot CRM to sync contact data.',
        featureFlag: 'hubspot_integration',
        settingsPath: '/settings/integrations#/hubspot',
        overview:
            'Integrate HubSpot with Voxie to sync contacts from a HubSpot Workflow into Voxie as an External List. This External List can be used immediately in Voxie for personalized SMS or MMS campaigns such as quick blast or evergreen campaigns.',
        features: ['Add HubSpot contacts to Voxie external lists', 'Map HubSpot properties to Voxie contact fields'],
        logo: defineAsyncComponent(() => import('./logos/HubspotLogo.vue')),
        url: 'https://hubspot.com',
        getStatus: (teamId) => teamsClient.getHubspotConnectionStatus(teamId),
        documentation: defineAsyncComponent(() => import('../integrations/hubspot/HubspotDocumentation.vue')),
    },
    {
        slug: 'mparticle',
        name: 'mParticle',
        tagline: 'Sync mParticle audiences and events with Voxie.',
        overview:
            'Connect mParticle to Voxie to leverage your enterprise customer data platform for SMS marketing. Stream customer events, attributes, and audiences from mParticle to trigger personalized messaging at scale.',
        features: [
            'Real-time event streaming between mParticle and Voxie',
            'Audience segment synchronization via External Lists',
            'Automatic contact creation from mParticle registration events',
            'Subscription status synchronization',
            'Link click tracking for SMS campaigns',
        ],
        logo: defineAsyncComponent(() => import('./logos/MparticleLogo.vue')),
        url: 'https://mparticle.com',
        documentation: defineAsyncComponent(() => import('../integrations/mparticle/MparticleDocumentation.vue')),
    },
    {
        slug: 'revel',
        name: 'Revel',
        tagline: 'Sync customers and orders from your Revel POS system.',
        featureFlag: 'revel_integration',
        settingsPath: '/settings/integrations#/revel',
        overview:
            'Connect your Revel point of sale system to Voxie to automatically sync customer data and purchase information.',
        features: [
            'Automatic customer sync from POS transactions',
            'Real-time purchase data collection',
            'Customer phone number, email, and name capture',
            'Purchase tracking with last order date and amount',
        ],
        logo: defineAsyncComponent(() => import('./logos/RevelLogo.vue')),
        url: 'https://support.revelsystems.com',
        getStatus: (teamId) => teamsClient.getRevelStatus(teamId),
        documentation: defineAsyncComponent(() => import('../integrations/revel/RevelDocumentation.vue')),
    },
    {
        slug: 'shopify',
        name: 'Shopify',
        tagline: 'Sync customers and orders from your Shopify store.',
        settingsPath: '/settings/integrations#/shopify',
        overview:
            'Connect your Shopify store to Voxie to automatically sync customer data and order information. When customers make purchases, their information flows directly into Voxie, enabling personalized SMS marketing based on their shopping behavior.',
        features: [
            'Automatic customer sync when orders are placed',
            'Real-time order data collection',
            'Customer phone number and email capture',
        ],
        logo: defineAsyncComponent(() => import('./logos/ShopifyLogo.vue')),
        url: 'https://shopify.com',
    },
    {
        slug: 'square',
        name: 'Square',
        tagline: 'Sync contacts and conversions from your Square POS system.',
        overview:
            'Connect your Square point of sale system to Voxie to automatically sync customer contacts and conversion data. Orders are pulled hourly, creating contacts with an integration entry point for targeted SMS marketing.',
        features: [
            'Hourly order sync from Square POS',
            'Automatic contact creation from transactions',
            'Customer phone number, email, and name capture',
            'Conversion tracking with order details',
        ],
        logo: defineAsyncComponent(() => import('./logos/SquareLogo.vue')),
        url: 'https://squareup.com',
    },
];

export function getIntegrationBySlug(slug: string): Integration | undefined {
    return integrations.find((integration) => integration.slug === slug);
}
